import { VOICE_PARTS } from '@corocanto/scoring';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invitationLink, useInvitations, useRenewInvitation, useRevokeInvitation } from '../api/members';
import { useMembers } from '../api/songs';
import type { Invitation, Member, VoicePart } from '../api/types';
import { isDirector, useMembership } from '../auth/session';
import { Person } from '../components/Avatar';
import { FilterChips } from '../components/FilterChips';
import { ErrorView } from '../components/Guards';
import { CopyButton, EditMemberDialog, InviteDialog, PitchDialog, RemoveMemberDialog } from '../components/MemberDialogs';
import { avatarKind } from '../lib/people';
import ui from '../styles/ui.module.css';
import styles from './MembersPage.module.css';

const iconProps = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true, focusable: false } as const;
const EditIcon = () => (
  <svg {...iconProps}>
    <path d="M4 20h4L19 9l-4-4L4 16v4z" />
    <path d="M13.5 6.5l4 4" />
  </svg>
);
const RemoveIcon = () => (
  <svg {...iconProps}>
    <circle cx={12} cy={12} r={8.5} />
    <path d="M8 12h8" />
  </svg>
);

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The choir's members with their parts and roles, for every member, with quick filters by section.
 * The director also invites people, changes parts and roles, removes members, sees the invitations
 * pending and changes the reference pitch (wireframe 16). Only the director sees e-mail addresses
 * (the server sends them only to the director).
 */
export function MembersPage() {
  const { t } = useTranslation();
  const membership = useMembership();
  const choirId = membership?.choirId ?? '';
  const director = isDirector(membership);
  const members = useMembers(membership?.choirId);
  const invitations = useInvitations(membership?.choirId, director);
  const [section, setSection] = useState<VoicePart | null>(null);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [pitchOpen, setPitchOpen] = useState(false);
  const list = members.data ?? [];
  const shown = section ? list.filter((m) => m.defaultPart === section) : list;
  const pending = invitations.data ?? [];

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <div className={ui.eyebrow}>{membership?.choirName}</div>
          <h1>{t('members.title')}</h1>
          {members.data && (
            <div className={ui.subtitle} data-testid="members-summary">
              {t('members.count', { count: list.length })}
              {director && pending.length > 0 && <> · {t('members.pending', { count: pending.length })}</>}
              {director && membership && (
                <>
                  {' · '}
                  {t('members.pitch', { hz: membership.referencePitchHz })}{' '}
                  <button type="button" className={ui.linkButton} onClick={() => setPitchOpen(true)}>
                    {t('members.changePitch')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        {director && (
          <button type="button" className={`${ui.button} ${ui.primary}`} onClick={() => setInviting(true)}>
            {t('members.invite')}
          </button>
        )}
      </div>
      <FilterChips
        label={t('members.filter')}
        value={section}
        onChange={setSection}
        testId="member-filters"
        options={[
          { key: null, label: t('events.all'), count: list.length },
          ...VOICE_PARTS.map((part) => ({ key: part, label: t(`parts.${part}`), count: list.filter((m) => m.defaultPart === part).length })),
        ]}
      />
      <section className={ui.card}>
        {members.isPending && <p className={ui.muted}>{t('common.loading')}</p>}
        {members.isError && <ErrorView onRetry={() => void members.refetch()} />}
        {members.data && (
          <table className={ui.table}>
            <thead>
              <tr>
                <th>{t('members.colName')}</th>
                {director && <th>{t('members.colEmail')}</th>}
                <th>{t('members.colPart')}</th>
                <th>{t('members.colRoles')}</th>
                {director && <th aria-hidden="true" />}
              </tr>
            </thead>
            <tbody>
              {shown.map((m) => (
                <tr key={m.userId} data-testid="member-row">
                  <td>
                    <Person name={m.displayName} kind={avatarKind(m.roles)} leadsSection={m.leadsSection} size="small">
                      {m.displayName}
                    </Person>
                  </td>
                  {director && <td className={ui.muted}>{m.email}</td>}
                  <td>{m.defaultPart ? t(`parts.${m.defaultPart}`) : '–'}</td>
                  <td>
                    <span className={ui.chipRow}>
                      {m.roles.map((r) => (
                        <span key={r} className={`${ui.chip} ${r === 'CHORIST' ? ui.chipNeutral : ui.chipSoft}`}>
                          {t(`roles.${r}`)}
                        </span>
                      ))}
                    </span>
                  </td>
                  {director && (
                    <td className={styles.rowActions}>
                      {/* The director's own row (and any director's) is changed by the administrator only. */}
                      {!m.roles.includes('DIRECTOR') && (
                        <>
                          <button
                            type="button"
                            className={styles.iconButton}
                            aria-label={`${t('members.edit')}: ${m.displayName}`}
                            data-tip={t('members.edit')}
                            onClick={() => setEditing(m)}
                          >
                            <EditIcon />
                          </button>
                          <button
                            type="button"
                            className={styles.iconButton}
                            aria-label={`${t('members.remove')}: ${m.displayName}`}
                            data-tip={t('members.remove')}
                            onClick={() => setRemoving(m)}
                          >
                            <RemoveIcon />
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {director && pending.length > 0 && <PendingInvitations choirId={choirId} invitations={pending} />}

      {director && membership && (
        <>
          <InviteDialog choirId={choirId} open={inviting} onClose={() => setInviting(false)} />
          <EditMemberDialog choirId={choirId} member={editing} onClose={() => setEditing(null)} />
          <RemoveMemberDialog choirId={choirId} choirName={membership.choirName} member={removing} onClose={() => setRemoving(null)} />
          <PitchDialog choirId={choirId} current={membership.referencePitchHz} open={pitchOpen} onClose={() => setPitchOpen(false)} />
        </>
      )}
    </div>
  );
}

/** Invitations neither used nor revoked, with their expiry: copy the link again, make a new one, or revoke. */
function PendingInvitations({ choirId, invitations }: { choirId: string; invitations: Invitation[] }) {
  const { t } = useTranslation();
  const renew = useRenewInvitation(choirId);
  const revoke = useRevokeInvitation(choirId);
  return (
    <section className={ui.card} data-testid="invitations">
      <h2 className={ui.cardTitle}>{t('members.invitations.title')}</h2>
      <ul className={styles.invitations}>
        {invitations.map((invitation) => {
          const days = Math.ceil((new Date(invitation.expiresAt).getTime() - Date.now()) / DAY_MS);
          const facts = [
            t(`parts.${invitation.part}`),
            ...(invitation.chorist ? [t('roles.CHORIST')] : []),
            ...(invitation.sectionLeader ? [t('roles.SECTION_LEADER')] : []),
            ...(invitation.email ? [t('members.invitations.emailGiven')] : []),
          ];
          return (
            <li key={invitation.id} className={styles.invitation} data-testid="invitation-row">
              <div>
                <div className={styles.invitee}>{invitation.displayName}</div>
                <div className={ui.muted}>{facts.join(' · ')}</div>
              </div>
              <span className={invitation.expired ? styles.expired : ui.muted}>
                {invitation.expired ? t('members.invitations.expired') : t('members.invitations.expiresIn', { count: Math.max(days, 1) })}
              </span>
              <div className={styles.invitationButtons}>
                {!invitation.expired && <CopyButton text={invitationLink(invitation.token)} label={t('members.invitations.copyLink')} />}
                <button type="button" className={ui.button} disabled={renew.isPending} onClick={() => renew.mutate(invitation.id)}>
                  {t('members.invitations.newLink')}
                </button>
                <button
                  type="button"
                  className={`${ui.button} ${styles.revoke}`}
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(invitation.id)}
                >
                  {t('members.invitations.revoke')}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
